/**
 * Withdrawal Restriction Service
 * Implements VIP level-based withdrawal restrictions based on balance thresholds
 */

// VIP Level Balance Limits
export const VIP_WITHDRAWAL_LIMITS = {
  'VIP1': 800,
  'VIP2': 2500,
  'VIP3': 8000,
  'VIP4': 10000
};

/**
 * Check if user can withdraw based on VIP level and balance
 * @param {string} vipLevel - User's VIP level (VIP1, VIP2, VIP3, VIP4)
 * @param {number} balance - User's current balance
 * @returns {Object} - { canWithdraw: boolean, reason: string, nextLevel: string, limit: number }
 */
export const checkWithdrawalRestriction = (vipLevel, balance) => {
  const level = vipLevel || 'VIP1';
  const limit = VIP_WITHDRAWAL_LIMITS[level] || VIP_WITHDRAWAL_LIMITS['VIP1'];
  const userBalance = parseFloat(balance) || 0;

  // Check if balance exceeds the limit for current VIP level
  if (userBalance > limit) {
    // VIP4 users should contact customer care (max level reached)
    if (level === 'VIP4') {
      return {
        canWithdraw: false,
        reason: 'Contact Customer Care for withdrawal assistance',
        message: 'Contact Customer Care for withdrawal assistance',
        action: 'customer_care',
        limit: limit,
        currentLevel: level
      };
    }

    // For VIP1-3, suggest upgrade to next level
    const levelNumber = parseInt(level.replace('VIP', ''));
    const nextLevel = `VIP${levelNumber + 1}`;
    const nextLimit = VIP_WITHDRAWAL_LIMITS[nextLevel];

    return {
      canWithdraw: false,
      reason: `Upgrade to ${nextLevel} to withdraw. Current limit: $${limit.toLocaleString()}`,
      message: `Upgrade to ${nextLevel} to withdraw. Current limit: $${limit.toLocaleString()}`,
      action: 'upgrade',
      limit: limit,
      nextLevel: nextLevel,
      nextLimit: nextLimit,
      currentLevel: level
    };
  }

  // Balance is within limit, can withdraw
  return {
    canWithdraw: true,
    limit: limit,
    currentLevel: level
  };
};

/**
 * Get withdrawal limit for a specific VIP level
 * @param {string} vipLevel - VIP level
 * @returns {number} - Maximum allowed balance for withdrawal
 */
export const getWithdrawalLimit = (vipLevel) => {
  const level = vipLevel || 'VIP1';
  return VIP_WITHDRAWAL_LIMITS[level] || VIP_WITHDRAWAL_LIMITS['VIP1'];
};

